/**
 * Copyright (C) 2019 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import React from 'react';
import get from 'lodash/get';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { RouteComponentProps } from 'react-router';
import IconButton from '@mui/material/IconButton';
import ArrowBack from '@mui/icons-material/ArrowBack';
import { RootState } from '../../../store';
import StyledProps from '../../common/theme/AppThemeInterface';
import { transformNetworkName } from '../../../utils/StringUtils';
import ExportIcon from './ExportIcon';
import { InstallationType, NetworkOrigin } from '../../../shared/entities/terminal';
import {
  terminalTypeDisplayNameFromTerminalTypeLabel,
  getSelectedTerminalTypeLabelBasedOnTab,
  getOtherTerminalOnAircraft,
} from '../../../utils/TerminalUtilities';
import { Button } from '@mui/material';

import { buttonExportStyle } from './ButtonExportStyle';

const getBeamNameLabel = (beamName: string, networkOrigin: NetworkOrigin) => {
  return `${beamName} / ${transformNetworkName(networkOrigin)}`;
};

interface FullTailViewPrintViewHeaderContentProps extends StateProps {}

export class FullTailViewPrintViewHeaderContent extends React.PureComponent<FullTailViewPrintViewHeaderContentProps> {
  onBackButtonClick = () => {
    this.props.history.goBack();
  };

  render() {
    const { terminal, selectedTerminalTabIndex } = this.props;

    const hasTerminal = terminal !== null;
    const aircraftTailNumber = get(terminal, 'terminalDisplayName', '');
    const installationType = get(terminal, 'installationType');
    const selectedTerminalType = getSelectedTerminalTypeLabelBasedOnTab(selectedTerminalTabIndex, terminal);

    let label = aircraftTailNumber;
    let beamLabel: string | JSX.Element = getBeamNameLabel(
      get(terminal, 'beamDisplayName', ''),
      get(terminal, 'networkOrigin', '') as NetworkOrigin,
    );

    if (installationType === InstallationType.DUAL_TERMINAL || installationType === InstallationType.KU_PLUS_KA) {
      // change the label to add the antenna type if needed
      if (selectedTerminalType !== undefined) {
        label = `${aircraftTailNumber} - ${terminalTypeDisplayNameFromTerminalTypeLabel(
          getSelectedTerminalTypeLabelBasedOnTab(selectedTerminalTabIndex, terminal),
        )}`;

        // change the beam name and network origin if the other terminal is selected
        if (selectedTerminalType !== get(terminal, 'terminalTypeLabel')) {
          let otherTerminal = getOtherTerminalOnAircraft(terminal);
          beamLabel = getBeamNameLabel(get(otherTerminal, 'beamDisplayName'), get(otherTerminal, 'networkOrigin'));
        }
      } else if (terminal && installationType === InstallationType.KU_PLUS_KA) {
        // overview tab selected
        let otherTerminal = getOtherTerminalOnAircraft(terminal);
        beamLabel = (
          <span>
            <b>{`${terminalTypeDisplayNameFromTerminalTypeLabel(terminal.terminalTypeLabel)}: `}</b>
            {`${getBeamNameLabel(get(terminal, 'beamDisplayName'), get(terminal, 'networkOrigin'))}, `}
            <b>{`${terminalTypeDisplayNameFromTerminalTypeLabel(otherTerminal.terminalTypeLabel)}: `}</b>
            {`${getBeamNameLabel(get(otherTerminal, 'beamDisplayName'), get(otherTerminal, 'networkOrigin'))}`}
          </span>
        );
      }
    }

    return (
      <React.Fragment>
        <BackButtonStyledWrapper>
          <IconButton className="ftv__header__back--container" onClick={this.onBackButtonClick}>
            <ArrowBack />
          </IconButton>
        </BackButtonStyledWrapper>
        <PrintLabel data-test="ftv__export__header__title">Full Tail Report {label}</PrintLabel>
        <DataLabel data-test="ftv__export__header__beamInfo">{hasTerminal && beamLabel}</DataLabel>
        <Button
          color="secondary"
          disableRipple
          sx={{
            ...buttonExportStyle,
            marginLeft: '24px',
            '@media print': {
              display: 'none',
            },
          }}
          onClick={() => {
            window.print();
          }}
        >
          Print <ExportIcon />
        </Button>
      </React.Fragment>
    );
  }
}

const mapStateToProps = (root: RootState, ownProps: RouteComponentProps) => {
  return {
    ...ownProps,
    terminal: root.terminals.tailDetailsPanelTerminal,
    selectedTerminalTabIndex: root.ui.overlaysContextUiState[root.ui.overlaysCurrentContext].selectedTerminalTabIndex,
  };
};
type StateProps = ReturnType<typeof mapStateToProps>;

export default connect(mapStateToProps)(FullTailViewPrintViewHeaderContent);

const BackButtonStyledWrapper = styled.span`
  @media print {
    display: none;
  }

  .ftv__header__back--container {
    color: ${({ theme }: StyledProps) => theme.primaryTextColor};
    margin-left: 6px;
    margin-right: 6px;
  }
`;

const PrintLabel = styled.h1`
  @media print {
    color: black !important;
  }
`;

const DataLabel = styled.h2`
  margin-left: auto;
  font-family: 'Uni Neue Light', sans-serif;
  color: #ffffff;
  font-size: 14px;
  letter-spacing: 0.05rem;

  b {
    font-family: 'Uni Neue Heavy', sans serif;
  }

  @media print {
    color: black !important;
    margin-right: 6px !important;
  }
`;
